import { getClientApplicationConfig } from '@hc-frontend/core-context';
import { Email as EmailValidation } from '@hc-frontend/core-types';
import { ErrorMessage, Link } from '@hc-frontend/core-ui';
import { ButtonWithTracking } from '@hc-frontend/shells-shared';
import { Dispatch, FormEvent, SetStateAction, useState } from 'react';
import { z } from 'zod';

import { EMAIL, ResultStatus } from './constants';
import { useSubsidyCalculator } from './provider';

const FormSchema = z.object({
  email: EmailValidation,
});

type EmailViewProps = {
  minLoadingTime: number;
  setStatus: Dispatch<SetStateAction<ResultStatus>>;
};

export function EmailView({ setStatus, minLoadingTime }: EmailViewProps) {
  const config = getClientApplicationConfig();
  const { answers, setAnswer, setSubsidy } = useSubsidyCalculator();
  const [email, setEmail] = useState(answers[EMAIL] ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const validation = FormSchema.safeParse({ email });
    if (!validation.success) {
      setError('Please enter a valid email.');
      return;
    }

    setAnswer(EMAIL, validation.data.email);
    setStatus('loading');

    const url = `${config.core.appBaseUrl}/mp2/api/widgets/subsidy/`;
    const params = new URLSearchParams();
    params.append(EMAIL, validation.data.email);

    for (const [key, value] of Object.entries(answers)) {
      params.append(key, String(value));
    }

    // At least wait for the minLoadingTime to show all the loading animations.
    const [data] = await Promise.allSettled([
      fetch(`${url}?${params.toString()}`).then((response) => response.json()),
      new Promise((r) => setTimeout(r, minLoadingTime)),
    ]);

    if (data.status !== 'fulfilled') {
      setStatus('error');
      return;
    }

    if (data.value.result !== 'success') {
      setStatus('error');
      return;
    }

    setSubsidy(data.value.body);
    setStatus('success');
  };

  const ctaText = 'Get Results';

  return (
    <div className="bg-gradient-to-br from-tertiary-900 to-primary-900 rounded-2xl p-24-32 animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both">
      <div className="flex justify-center items-center flex-col max-w-[620px] mx-auto">
        <p className="hc-typography--heading-three text-gray-0 text-center">
          Get Your Results
        </p>
        <p className="hc-typography--paragraph-sm text-gray-0 text-center">
          Enter your email to see your subsidy amount and potential savings
        </p>

        <form
          onSubmit={handleSubmit}
          className="w-full grid grid-cols-1 tablet:grid-cols-[2fr_1fr] mt-24-24 gap-8-8"
        >
          <label className="hc-form-control relative">
            <input
              placeholder="Enter your email"
              aria-label="Email"
              type="email"
              name={EMAIL}
              className="w-full outline-none"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />
          </label>

          <ButtonWithTracking
            id="subsidy-calculator-results-button"
            variant="primary-card-small"
            type="submit"
            className="w-full px-32-48 whitespace-nowrap flex justify-center"
            metricTags={{}}
            name="action"
            value="results"
          >
            {ctaText}
          </ButtonWithTracking>
        </form>

        {error ? (
          <div className="mt-4-4">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        <p className="hc-typography--disclaimer text-gray-0 mt-16-16 text-center">
          By providing your email and clicking “{ctaText}”, you agree to receive
          communications from Healthcare.com about health insurance options,
          savings opportunities, and related services. No purchase is required
          to receive this information. You may opt-out at any time by emailing{' '}
          <Link
            id="subsidy-calculator-privacy-policy"
            href="mailto:privacy@healthcare.com"
            rel="noreferrer noopener"
            target="_blank"
            className="text-secondary-900 underline"
          >
            privacy@healthcare.com
          </Link>{' '}
          or{' '}
          <Link
            id="subsidy-calculator-data-request"
            href="https://www.healthcare.com/data-request/request-form/"
            rel="noreferrer noopener"
            target="_blank"
            className="text-secondary-900 underline"
          >
            visit our data request page
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
